import { useContext } from 'react';
import { Box, IconButton } from '@mui/material';
import { DateTime, Interval } from 'luxon';
import { formatPhoneNumber } from '../utils/PhoneNumber';
import { stillSendSms, getSMSStatus, getSMSCount, getAutomaticSMSStatus } from '../helpers/SMS';
import StoreContext from '../context/StoreContext';
import SmsContext from '../context/SmsContext';
import Info from '@mui/icons-material/Info';
import Edit from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CalendarIcon from '../utils/CalendarIcon';
import PromisedTime from '../components/PromisedTime';
import ResponsiveTooltip from '../components/ResponsiveTooltip';
import Switches from '../components/Switches';
import CourtesyRidesButton from '../components/CourtesyRidesButton';

const NEXT_APPT_SERVICES = process.env.REACT_APP_NEXT_APPT_SERVICES.toUpperCase().split(',');
const VEHICLE_READY_DELAY = process.env.REACT_APP_VEHICLE_READY_DELAY

export const useMRTableCols = ({
  handleLyftDialog,
  handleNextApptCalendar,
  setInfoPopup,
  setOpenInfoPopup,
  notifyCustomer,
  openPhoneEdit,
  toggleRow,
  openRows
}) => {

  const { courtesyLyft, promisedTimeSms, vehicleReadySms, time_zone, nextAppt, globalBeback } = useContext(StoreContext)
  const { sendingSms, messageType } = useContext(SmsContext)

  return [
    {
      id: "collapseRow",
      label: "",
      cellContent: (rowData, index) => (
        <IconButton
          aria-label="expand row"
          size="small"
          onClick={() => toggleRow(index)}
        >
          {!!openRows[index] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      ),
      sortable: false,
      showCol: true,
      rowClassName: 'pl-2 pr-1'
    },
    {
      id: "editPhone",
      label: "",
      cellContent: (rowData, index) => (
        <IconButton
          aria-label="edit phone"
          size="small"
          onClick={() => openPhoneEdit(rowData, index)}
        >
          <Edit />
        </IconButton>
      ),
      sortable: false,
      showCol: true,
      rowClassName: 'px-0'
    },
    {
      id: "courtesyRides",
      label: "",
      cellContent: (rowData) => ( 
        <CourtesyRidesButton rowData={rowData} handleLyftDialog={handleLyftDialog} />
      ),
      sortable: false,
      showCol: !!courtesyLyft,
      headerClassName: 'px-2',
      rowClassName: 'px-2'
    },
    {
      id: "Name",
      label: "NAME",
      sortable: true,
      cellContent: (rowData) => (
        <ResponsiveTooltip title={rowData.Name}>
          <Box className={`truncate ${!!promisedTimeSms ? 'table-name-pt-col' : 'table-name-col'}`}>
            {rowData.Name}
          </Box>
        </ResponsiveTooltip>
      ),
      showCol: true,
      headerClassName: 'pr-1',
      rowClassName: 'pr-1'
    },
    {
      id: "Phone",
      label: "MOBILE",
      sortable: true,
      cellContent: (rowData) => (
        <Box className='table-phone-col'>
          {formatPhoneNumber(rowData.Phone)}
        </Box>
      ),
      showCol: true,
      headerClassName: 'px-1',
      rowClassName: 'px-1'
    },
    {
      id: "Vehicle",
      label: "VEHICLE",
      sortable: true,
      cellContent: (rowData) => (
        <ResponsiveTooltip title={rowData.Vehicle}>
          <Box className={`truncate ${!!promisedTimeSms ? 'table-vehicle-col-pt' : 'table-vehicle-col'}`}>
            {rowData.Vehicle}
          </Box>
        </ResponsiveTooltip>
      ),
      showCol: true,
      headerClassName: 'px-0',
      rowClassName: 'px-0'
    },
    {
      id: "DateField",
      label: (
        <div className='table-title-text'>
          <label>Promise Time</label>{" "}
          <Info
            className='table-icon-info'
            onClick={() => {
              setInfoPopup("promiseTime");
              setOpenInfoPopup(true);
            }}
          />
        </div>
      ),
      sortable: true,
      cellContent: (rowData) => (
        <Box className='w-100 flex justify-center'>
          <PromisedTime rowData={rowData} />
        </Box>
      ),
      showCol: !!+promisedTimeSms,
      headerClassName: 'px-1',
      rowClassName: 'table-pt-col px-1'
    },
    {
      id: "CallStore",
      label: (
        <div className='table-title-text'>
          <label>Call Store</label>{" "}
          <Info
            className='table-icon-info'
            onClick={() => {
              setInfoPopup("callStore");
              setOpenInfoPopup(true);
            }}
          />
        </div>
      ),
      sortable: false,
      cellContent: (rowData) => (
        <Switches
          type="call"
          text="CALL"
          title="Call store"
          isSendingSms={(sendingSms?.includes(rowData.WO) && messageType === 'callStoreMessage')}
          isRowSendingSms={sendingSms?.includes(rowData.WO)}
          hasSent={stillSendSms("call", rowData)}
          disabled={false}
          data={rowData}
          sms="callStoreMessage"
          sendSMS={notifyCustomer}
          status={getSMSStatus(rowData.SmsData?.callStoreMessage)}
          messagesCount={getSMSCount(rowData.SmsData?.callStoreMessage)}
        />
      ),
      headerClassName: 'px-2',
      showCol: true,
    },
    {
      id: "BeBack",
      label: (
        <div className='table-title-text'>
          <label>BeBack</label>{" "}
          <Info
            className='table-icon-info'
            onClick={() => {
              setInfoPopup("beBack");
              setOpenInfoPopup(true);
            }}
          />
        </div>
      ),
      sortable: false,
      cellContent: (rowData) => (
        <Switches
          type="beBack"
          text="BACK"
          title="BeBack"
          isSendingSms={(sendingSms?.includes(rowData.WO) && messageType === 'beBackMessage')}
          isRowSendingSms={sendingSms?.includes(rowData.WO)}
          hasSent={stillSendSms("beback", rowData, globalBeback)}
          disabled={!globalBeback && !rowData.IsMarshal}
          data={rowData}
          sms="beBackMessage"
          sendSMS={notifyCustomer}
          status={getSMSStatus(rowData.SmsData?.beBackMessage)}
          messagesCount={getSMSCount(rowData.SmsData?.beBackMessage)}
        />
      ),
      headerClassName: 'px-2',
      showCol: true,
    },
    {
      id: "VehicleReady",
      label: (
        <div className='table-title-text'>
          <label>Vehicle Ready</label>{" "}
          <Info
            className='table-icon-info'
            onClick={() => {
              setInfoPopup("vehicleReady");
              setOpenInfoPopup(true);
            }}
          />
        </div>
      ),
      sortable: false,
      cellContent: (rowData) => {
        const sendingAutomaticSMS = vehicleReadySms && rowData.cf_completed_timestamp && getAutomaticSMSStatus(rowData.SmsData?.vehicleReadyMessage);

        const now = DateTime.now().toUTC().setZone(time_zone);
        const completedTimestamp = DateTime.fromISO(rowData.cf_completed_timestamp).toUTC().setZone(time_zone);
        const sms_eta = completedTimestamp.plus({ seconds: VEHICLE_READY_DELAY });
        const timeWhenSendingSMS = Interval.fromDateTimes(completedTimestamp, sms_eta);

        return (
          <Switches
            type="vehicleReady"
            text="READY"
            title="Vehicle ready"
            isSendingSms={(sendingSms?.includes(rowData.WO) && messageType === 'vehicleReadyMessage')}
            isRowSendingSms={sendingSms?.includes(rowData.WO)}
            hasSent={stillSendSms("ready", rowData)}
            disabled={sendingAutomaticSMS}
            sms_eta={sms_eta}
            countdown={Boolean(timeWhenSendingSMS?.contains(now) && vehicleReadySms)}
            data={rowData}
            sms="vehicleReadyMessage"
            sendSMS={notifyCustomer}
            status={getSMSStatus(rowData.SmsData?.vehicleReadyMessage)}
            messagesCount={getSMSCount(rowData.SmsData?.vehicleReadyMessage)}
          />
        );
      },
      headerClassName: 'px-2',
      showCol: true,
    },
    {
      id: "appt_calendar",
      label: "Next Appt",
      cellContent: (rowData) => {
        const isAvailable = rowData.order_details?.some(item =>
          NEXT_APPT_SERVICES.some(service => item.description.includes(service)) || item.dept_group_no === 100
        ) && rowData.Phone;

        return (
          <IconButton
            onClick={() => handleNextApptCalendar(rowData)}
            disabled={!isAvailable}
          >
            <CalendarIcon successColor={rowData?.NextAppointmentId} disabled={!isAvailable} />
          </IconButton>
        );
      },
      sortable: false,
      showCol: !!+nextAppt,
    }
  ];
};